import { useState, useCallback, useEffect, useRef } from 'react';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

// ============================================================================
// Cache Configuration
// ============================================================================

const POI_CACHE_KEY_PREFIX = 'safaqat24.poi.cache';
const POI_CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const COORDINATE_PRECISION = 4; // Decimal places for cache key (approx 11m precision)

interface CachedPOIData {
  places: NearbyPlace[];
  placesByCategory: Record<PlaceCategory, NearbyPlace[]>;
  nearestByCategory: Record<PlaceCategory, NearbyPlace | null>;
  timestamp: number;
  searchRadius: number;
}

/**
 * Generate a cache key based on coordinates (rounded for proximity matching)
 */
function generateCacheKey(lat: number, lng: number): string {
  const roundedLat = lat.toFixed(COORDINATE_PRECISION);
  const roundedLng = lng.toFixed(COORDINATE_PRECISION);
  return `${POI_CACHE_KEY_PREFIX}.${roundedLat},${roundedLng}`;
}

/**
 * Read cached POI data from localStorage
 */
function readCachedPOI(lat: number, lng: number, searchRadius: number): CachedPOIData | null {
  try {
    const key = generateCacheKey(lat, lng);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const data: CachedPOIData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired
    if (now - data.timestamp > POI_CACHE_EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if search radius is the same or smaller (cached data is still valid)
    if (data.searchRadius < searchRadius) {
      return null; // Need to fetch with larger radius
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * Write POI data to localStorage cache
 */
function writeCachedPOI(
  lat: number,
  lng: number,
  data: Omit<CachedPOIData, 'timestamp'>
): void {
  try {
    const key = generateCacheKey(lat, lng);
    const cacheData: CachedPOIData = {
      ...data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    // localStorage might be full, try to clean old entries
    cleanOldPOICache();
    try {
      const key = generateCacheKey(lat, lng);
      localStorage.setItem(key, JSON.stringify({ ...data, timestamp: Date.now() }));
    } catch {
      console.warn('Failed to cache POI data:', e);
    }
  }
}

/**
 * Clean old POI cache entries to free up space
 */
function cleanOldPOICache(): void {
  try {
    const keysToRemove: string[] = [];
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(POI_CACHE_KEY_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const data: CachedPOIData = JSON.parse(cached);
            if (now - data.timestamp > POI_CACHE_EXPIRY_MS) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key!);
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore cleanup errors
  }
}

// ============================================================================
// Types matching Android NearbyPlacesRepository
// ============================================================================

export type PlaceCategory =
  | 'EDUCATION'
  | 'HEALTHCARE'
  | 'SHOPPING'
  | 'DINING'
  | 'RECREATION'
  | 'WORSHIP'
  | 'FINANCE'
  | 'TRANSPORTATION';

export interface NearbyPlace {
  id: string;
  name: string;
  nameArabic: string;
  address: string;
  latitude: number;
  longitude: number;
  type: string;
  category: PlaceCategory;
  distanceMeters: number;
  distanceKm: number;
  walkingMinutes: number;
  drivingMinutes: number;
  rating: number | null;
  reviewCount: number;
  icon?: string;
  isOpen?: boolean;
}

export interface NearbyPlacesResult {
  places: NearbyPlace[];
  category: PlaceCategory;
  searchCenter: { lat: number; lng: number };
  searchRadius: number;
}

interface UseNearbyPlacesOptions {
  searchRadius?: number; // meters, default 1500
  maxResults?: number;
}

// ============================================================================
// Constants
// ============================================================================

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Google Place types mapping to categories
const CATEGORY_PLACE_TYPES: Record<PlaceCategory, string[]> = {
  EDUCATION: ['school', 'university', 'primary_school', 'secondary_school'],
  HEALTHCARE: ['hospital', 'doctor', 'dentist', 'pharmacy', 'health'],
  SHOPPING: ['shopping_mall', 'supermarket', 'grocery_or_supermarket', 'store'],
  DINING: ['restaurant', 'cafe', 'bakery', 'food'],
  RECREATION: ['park', 'gym', 'spa', 'stadium', 'tourist_attraction'],
  // Saudi context: places of worship are mosques — surface them as "Mosques".
  WORSHIP: ['mosque', 'place_of_worship'],
  FINANCE: ['bank', 'atm', 'finance'],
  TRANSPORTATION: ['bus_station', 'subway_station', 'transit_station', 'gas_station'],
};

// Arabic translations for categories
const CATEGORY_ARABIC: Record<PlaceCategory, string> = {
  EDUCATION: 'تعليم',
  HEALTHCARE: 'رعاية صحية',
  SHOPPING: 'تسوق',
  DINING: 'مطاعم',
  RECREATION: 'ترفيه',
  WORSHIP: 'مساجد',
  FINANCE: 'بنوك',
  TRANSPORTATION: 'مواصلات',
};

// Place type Arabic translations
const PLACE_TYPE_ARABIC: Record<string, string> = {
  school: 'مدرسة',
  university: 'جامعة',
  primary_school: 'مدرسة ابتدائية',
  secondary_school: 'مدرسة ثانوية',
  hospital: 'مستشفى',
  doctor: 'عيادة',
  dentist: 'طبيب أسنان',
  pharmacy: 'صيدلية',
  health: 'مركز صحي',
  shopping_mall: 'مول',
  supermarket: 'سوبرماركت',
  grocery_or_supermarket: 'بقالة',
  store: 'متجر',
  restaurant: 'مطعم',
  cafe: 'مقهى',
  bakery: 'مخبز',
  food: 'طعام',
  park: 'حديقة',
  gym: 'نادي رياضي',
  spa: 'منتجع صحي',
  stadium: 'ملعب',
  tourist_attraction: 'معلم سياحي',
  mosque: 'مسجد',
  church: 'كنيسة',
  place_of_worship: 'مسجد',
  bank: 'بنك',
  atm: 'صراف آلي',
  bus_station: 'محطة باص',
  subway_station: 'محطة مترو',
  transit_station: 'محطة نقل',
  gas_station: 'محطة وقود',
};

// ============================================================================
// Script Loading (shared with usePlacesAutocomplete)
// ============================================================================

// Delegate to the single shared loader. Previously this injected its OWN script
// tag with a different `libraries` list (no `marker`); when it raced the shared
// loader on the property page, two Maps API copies loaded into separate realms,
// which broke AdvancedMarkerElement with "not an instance of MapsApiMap" and
// logged "...already defined" for the web components. One loader = one realm.
function loadGoogleMapsScript(): Promise<void> {
  return loadGoogleMaps();
}

/**
 * With `loading=async`, the `places` library can resolve before the core `Map`
 * constructor is registered — calling `new google.maps.Map()` then throws
 * "google.maps.Map is not a constructor". Wait until the constructor exists
 * (mirrors googleMapsLoader.ts `ensureCoreReady`). Without this, having more
 * than one hook consumer on a page reliably triggers the race.
 */
async function ensureMapsCoreReady(): Promise<void> {
  const g = () => (window as unknown as { google?: { maps?: { Map?: unknown; places?: { PlacesService?: unknown }; importLibrary?: (n: string) => Promise<unknown> } } }).google;
  // Gate on BOTH the Map constructor and places.PlacesService — this hook
  // instantiates a temp Map *and* a PlacesService immediately after, and under
  // loading=async those libraries resolve independently.
  const ready = () => typeof g()?.maps?.Map === 'function' && typeof g()?.maps?.places?.PlacesService === 'function';
  if (ready()) return;

  const importLib = g()?.maps?.importLibrary;
  if (typeof importLib === 'function') {
    await Promise.all([importLib('maps'), importLib('places')]);
    if (ready()) return;
  }

  await new Promise<void>((resolve, reject) => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (ready()) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - start > 5000) {
        clearInterval(timer);
        reject(new Error('Google Maps core/places library failed to initialize'));
      }
    }, 50);
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate distance between two points in meters using Haversine formula
 */
function calculateDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

/**
 * Estimate walking time in minutes (average 5 km/h)
 */
function estimateWalkingMinutes(distanceMeters: number): number {
  return Math.ceil(distanceMeters / 83.33);
}

/**
 * Estimate driving time in minutes (average 30 km/h in city)
 */
function estimateDrivingMinutes(distanceMeters: number): number {
  return Math.ceil(distanceMeters / 500);
}

/**
 * Get primary type from place types array
 */
function getPrimaryType(types: string[]): string {
  // Priority order for type selection
  const priorityTypes = [
    'school', 'university', 'hospital', 'mosque', 'shopping_mall',
    'supermarket', 'restaurant', 'park', 'gym', 'bank', 'bus_station',
    'subway_station', 'gas_station', 'pharmacy', 'doctor', 'cafe',
  ];

  for (const pType of priorityTypes) {
    if (types.includes(pType)) {
      return pType;
    }
  }
  return types[0] || 'place';
}

/**
 * Get Arabic name for a place type
 */
function getArabicPlaceType(type: string): string {
  return PLACE_TYPE_ARABIC[type] || type;
}

// ============================================================================
// Main Hook
// ============================================================================

export function useNearbyPlaces(options: UseNearbyPlacesOptions = {}) {
  const { searchRadius = 1500, maxResults = 20 } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [placesByCategory, setPlacesByCategory] = useState<Record<PlaceCategory, NearbyPlace[]>>({} as Record<PlaceCategory, NearbyPlace[]>);
  const [nearestByCategory, setNearestByCategory] = useState<Record<PlaceCategory, NearbyPlace | null>>({} as Record<PlaceCategory, NearbyPlace | null>);

  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const searchCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  // Initialize Google Places service
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      return;
    }

    let isMounted = true;

    async function init() {
      try {
        await loadGoogleMapsScript();
        // `loading=async`: ensure the Map constructor is actually ready before
        // we instantiate one (otherwise "google.maps.Map is not a constructor").
        await ensureMapsCoreReady();

        if (!isMounted) return;

        // Create hidden div for PlacesService
        if (!mapDivRef.current) {
          mapDivRef.current = document.createElement('div');
          mapDivRef.current.style.display = 'none';
          document.body.appendChild(mapDivRef.current);
        }

        // Create temporary map for PlacesService
        const tempMap = new google.maps.Map(mapDivRef.current, {
          center: { lat: 24.7136, lng: 46.6753 }, // Riyadh
          zoom: 10,
        });

        placesServiceRef.current = new google.maps.places.PlacesService(tempMap);
        setIsReady(true);
        setError(null);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize Places service');
        }
      }
    }

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Search for nearby places of a specific category
   */
  const searchByCategory = useCallback(
    async (
      lat: number,
      lng: number,
      category: PlaceCategory,
      radius?: number
    ): Promise<NearbyPlace[]> => {
      if (!placesServiceRef.current || !isReady) {
        return [];
      }

      const types = CATEGORY_PLACE_TYPES[category];
      if (!types || types.length === 0) {
        return [];
      }

      const location = new google.maps.LatLng(lat, lng);
      const effectiveRadius = radius || searchRadius;

      return new Promise((resolve) => {
        const request: google.maps.places.PlaceSearchRequest = {
          location,
          radius: effectiveRadius,
          type: types[0], // Use primary type
        };

        placesServiceRef.current!.nearbySearch(request, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            const nearbyPlaces: NearbyPlace[] = results
              .filter((place) => place.geometry?.location)
              .map((place) => {
                const placeLat = place.geometry!.location!.lat();
                const placeLng = place.geometry!.location!.lng();
                const distance = calculateDistanceMeters(lat, lng, placeLat, placeLng);
                const primaryType = getPrimaryType(place.types || []);

                return {
                  id: place.place_id || `${placeLat}-${placeLng}`,
                  name: place.name || 'Unknown',
                  nameArabic: getArabicPlaceType(primaryType),
                  address: place.vicinity || '',
                  latitude: placeLat,
                  longitude: placeLng,
                  type: primaryType,
                  category,
                  distanceMeters: distance,
                  distanceKm: Math.round(distance / 100) / 10,
                  walkingMinutes: estimateWalkingMinutes(distance),
                  drivingMinutes: estimateDrivingMinutes(distance),
                  rating: place.rating || null,
                  reviewCount: place.user_ratings_total || 0,
                  icon: place.icon,
                  isOpen: place.opening_hours?.isOpen?.() ?? undefined,
                };
              })
              .filter((place) => place.distanceMeters <= effectiveRadius)
              .sort((a, b) => a.distanceMeters - b.distanceMeters)
              .slice(0, maxResults);

            resolve(nearbyPlaces);
          } else {
            resolve([]);
          }
        });
      });
    },
    [isReady, searchRadius, maxResults]
  );

  /**
   * Search for schools nearby
   */
  const searchNearbySchools = useCallback(
    async (lat: number, lng: number, radius?: number) => {
      return searchByCategory(lat, lng, 'EDUCATION', radius || 2000);
    },
    [searchByCategory]
  );

  /**
   * Search for attractions (shopping, recreation, dining)
   */
  const searchNearbyAttractions = useCallback(
    async (lat: number, lng: number, radius?: number) => {
      const [shopping, recreation, dining] = await Promise.all([
        searchByCategory(lat, lng, 'SHOPPING', radius),
        searchByCategory(lat, lng, 'RECREATION', radius),
        searchByCategory(lat, lng, 'DINING', radius),
      ]);

      return [...shopping, ...recreation, ...dining]
        .sort((a, b) => a.distanceMeters - b.distanceMeters)
        .slice(0, maxResults);
    },
    [searchByCategory, maxResults]
  );

  /**
   * Search for services (healthcare, finance, worship, transportation)
   */
  const searchNearbyServices = useCallback(
    async (lat: number, lng: number, radius?: number) => {
      const [healthcare, finance, worship, transportation] = await Promise.all([
        searchByCategory(lat, lng, 'HEALTHCARE', radius || 2500),
        searchByCategory(lat, lng, 'FINANCE', radius),
        searchByCategory(lat, lng, 'WORSHIP', radius || 1000),
        searchByCategory(lat, lng, 'TRANSPORTATION', radius || 2000),
      ]);

      return [...healthcare, ...finance, ...worship, ...transportation]
        .sort((a, b) => a.distanceMeters - b.distanceMeters)
        .slice(0, maxResults);
    },
    [searchByCategory, maxResults]
  );

  /**
   * Get all nearby places grouped by category (with caching)
   */
  const getAllNearbyPlaces = useCallback(
    async (lat: number, lng: number, radius?: number, forceRefresh = false) => {
      if (!isReady) {
        setError('Places service not ready');
        return;
      }

      const effectiveRadius = radius || searchRadius;
      searchCenterRef.current = { lat, lng };

      // Check cache first (unless force refresh is requested)
      if (!forceRefresh) {
        const cached = readCachedPOI(lat, lng, effectiveRadius);
        if (cached) {
          setPlacesByCategory(cached.placesByCategory);
          setNearestByCategory(cached.nearestByCategory);
          setPlaces(cached.places);
          setError(null);
          return; // Return cached data without API call
        }
      }

      setIsLoading(true);
      setError(null);

      try {
        const categories: PlaceCategory[] = [
          'EDUCATION',
          'HEALTHCARE',
          'SHOPPING',
          'DINING',
          'RECREATION',
          'WORSHIP',
          'FINANCE',
          'TRANSPORTATION',
        ];

        const results = await Promise.all(
          categories.map((category) => searchByCategory(lat, lng, category, effectiveRadius))
        );

        const grouped: Record<PlaceCategory, NearbyPlace[]> = {} as Record<PlaceCategory, NearbyPlace[]>;
        const nearest: Record<PlaceCategory, NearbyPlace | null> = {} as Record<PlaceCategory, NearbyPlace | null>;
        let allPlaces: NearbyPlace[] = [];

        categories.forEach((category, index) => {
          grouped[category] = results[index];
          nearest[category] = results[index][0] || null;
          allPlaces = [...allPlaces, ...results[index]];
        });

        // Sort all places by distance
        allPlaces = allPlaces
          .sort((a, b) => a.distanceMeters - b.distanceMeters)
          .slice(0, maxResults * 2);

        setPlacesByCategory(grouped);
        setNearestByCategory(nearest);
        setPlaces(allPlaces);

        // Cache the results
        writeCachedPOI(lat, lng, {
          places: allPlaces,
          placesByCategory: grouped,
          nearestByCategory: nearest,
          searchRadius: effectiveRadius,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch nearby places');
      } finally {
        setIsLoading(false);
      }
    },
    [isReady, searchByCategory, maxResults, searchRadius]
  );

  /**
   * Clear all results
   */
  const clearResults = useCallback(() => {
    setPlaces([]);
    setPlacesByCategory({} as Record<PlaceCategory, NearbyPlace[]>);
    setNearestByCategory({} as Record<PlaceCategory, NearbyPlace | null>);
    searchCenterRef.current = null;
  }, []);

  return {
    // State
    isLoading,
    isReady,
    error,
    places,
    placesByCategory,
    nearestByCategory,
    searchCenter: searchCenterRef.current,

    // Search methods
    searchByCategory,
    searchNearbySchools,
    searchNearbyAttractions,
    searchNearbyServices,
    getAllNearbyPlaces,

    // Utilities
    clearResults,

    // Constants for consumers
    categories: Object.keys(CATEGORY_PLACE_TYPES) as PlaceCategory[],
    categoryArabic: CATEGORY_ARABIC,
  };
}

export default useNearbyPlaces;
